// StudentTable.jsx
import React, { useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import axios from "axios";
import "./Student.css";

const StudentModal = ({ show, onClose, onSubmit, formData, handleInputChange }) => {
    if (!show) return null;

    return (
        <div
            className="modal-overlay"
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0,0,0,0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
            }}
        >
            <div
                className="modal-content"
                style={{
                    backgroundColor: "white",
                    padding: "20px",
                    borderRadius: "8px",
                    width: "100%",
                    maxWidth: "400px",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                }}
            >
                <h2 style={{ marginBottom: "16px" }}>Add Student</h2>

                {["name", "email", "total_fee", "total_receipt", "fee_pending", "cohort", "counsellor_name"].map((field) => (
                    <input
                        key={field}
                        type="text"
                        name={field}
                        value={formData[field] || ""}
                        onChange={handleInputChange}
                        placeholder={field.replace(/_/g, " ").toUpperCase()}
                        style={{
                            marginBottom: "10px",
                            padding: "8px",
                            width: "100%",
                            boxSizing: "border-box",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                        }}
                    />
                ))}

                <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "10px" }}>
                    <button
                        onClick={onSubmit}
                        style={{
                            backgroundColor: "green",
                            color: "white",
                            padding: "8px 16px",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                        }}
                    >
                        Submit
                    </button>
                    <button
                        onClick={onClose}
                        style={{
                            padding: "8px 16px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            cursor: "pointer",
                        }}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

const StudentTable = () => {
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [cohortFilter, setCohort] = useState("");
    const [counsellorFilter, setCounsellorFilter] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        total_fee: "",
        total_receipt: "",
        fee_pending: "",
        cohort: "",
        counsellor_name: "",
    });

    useEffect(() => {
        fetchStudents();
    }, []);

    const fetchStudents = async () => {
        try {
            const res = await axios.get("https://api.admissionsmanipal.com/api/v1/student");
            setStudents(res.data?.data ?? []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        try {
            await axios.post("https://api.admissionsmanipal.com/api/v1/student", formData);
            setShowModal(false);
            setFormData({
                name: "",
                email: "",
                total_fee: "",
                total_receipt: "",
                fee_pending: "",
                cohort: "",
                counsellor_name: "",
            });
            setLoading(true);
            await fetchStudents();
        } catch (error) {
            console.error("Error submitting form:", error);
        }
    };

    const handleView = (student) => {
        sessionStorage.setItem("selectedStudentId", student.id);
        window.location.href = "/student-payments";
    };

    const cohorts = useMemo(() => Array.from(new Set(students.map((s) => s.cohort))).sort(), [students]);
    const counsellors = useMemo(() => Array.from(new Set(students.map((s) => s.counsellor_name))).sort(), [students]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return students.filter((s) => {
            const byCohort = cohortFilter ? s.cohort === cohortFilter : true;
            const byCounsellor = counsellorFilter ? s.counsellor_name === counsellorFilter : true;
            const bySearch = !q || s.name.toLowerCase().includes(q) || s.email.toLowerCase().includes(q);
            return byCohort && byCounsellor && bySearch;
        });
    }, [students, search, cohortFilter, counsellorFilter]);

    const columns = [
        { name: "ID", selector: (row) => row.id, width: "70px", sortable: true },
        { name: "Cohort", selector: (row) => row.cohort, sortable: true },
        { name: "Name", selector: (row) => row.name, sortable: true, grow: 2 },
        { name: "Email", selector: (row) => row.email, sortable: true, grow: 2 },
        {
            name: "Total Fee",
            selector: (row) => row.total_fee,
            sortable: true,
            right: true,
            format: (r) => r.total_fee.toLocaleString(),
        },
        {
            name: "Paid",
            selector: (row) => row.total_receipt,
            sortable: true,
            right: true,
            format: (r) => r.total_receipt.toLocaleString(),
        },
        {
            name: "Pending",
            selector: (row) => row.fee_pending,
            sortable: true,
            right: true,
            conditionalCellStyles: [
                {
                    when: (row) => row.fee_pending > 0,
                    style: { fontWeight: "600" },
                },
            ],
            format: (r) => r.fee_pending.toLocaleString(),
        },
        { name: "Counsellor", selector: (row) => row.counsellor_name },
        {
            name: "Actions",
            cell: (row) => (
                <button
                    onClick={() => handleView(row)}
                    style={{
                        padding: "4px 8px",
                        backgroundColor: "#007bff",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                    }}
                >
                    View
                </button>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        },
    ];

    const FilterBar = (
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
            <button
                onClick={() => setShowModal(true)}
                style={{
                    padding: "6px 12px",
                    backgroundColor: "green",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                }}
            >
                + Add Student
            </button>

            <select
                value={cohortFilter}
                onChange={(e) => setCohort(e.target.value)}
                style={{ padding: "4px 8px" }}
            >
                <option value="">All cohorts</option>
                {cohorts.map((c) => (
                    <option key={c}>{c}</option>
                ))}
            </select>

            <select
                value={counsellorFilter}
                onChange={(e) => setCounsellorFilter(e.target.value)}
                style={{ padding: "4px 8px" }}
            >
                <option value="">All counsellors</option>
                {counsellors.map((c) => (
                    <option key={c}>{c}</option>
                ))}
            </select>

            <input
                type="text"
                placeholder="Search name or email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ flex: "1 0 220px", padding: "4px 8px" }}
            />

            {(search || cohortFilter || counsellorFilter) && (
                <button
                    onClick={() => {
                        setSearch("");
                        setCohort("");
                        setCounsellorFilter("");
                    }}
                >
                    Clear
                </button>
            )}
        </div>
    );

    return (
        <>
            <div style={{ overflowY: "hidden" }}>
                <StudentModal
                    show={showModal}
                    onClose={() => setShowModal(false)}
                    onSubmit={handleSubmit}
                    formData={formData}
                    handleInputChange={handleInputChange}
                />
                <DataTable
                    columns={columns}
                    data={filtered}
                    progressPending={loading}
                    pagination
                    paginationPerPage={5}
                    highlightOnHover
                    subHeader
                    subHeaderComponent={FilterBar}
                    persistTableHead
                />
            </div>
        </>
    );
};

export default StudentTable;
